import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useQuantJob from './hooks/useQuantJob';
import JobStatusBar from './components/JobStatusBar';

const fmt = (v, n = 3) => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(n);
const pct = (v, n = 1) => (v == null || isNaN(v)) ? '—' : `${(Number(v) * 100).toFixed(n)}%`;
const numColor = (v) =>
  v == null || isNaN(v) ? 'text-gray-400'
  : v > 0 ? 'text-emerald-600 dark:text-emerald-400'
  : v < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400';

const VERDICT_COLORS = {
  REAL:      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  LIKELY:    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  UNCERTAIN: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  LUCKY:     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const BacktestResultPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { job, loading } = useQuantJob('backtest', jobId, 1500);

  if (loading && !job) return <div className="p-8 text-center text-sm text-gray-400">Loading job…</div>;
  if (!job) return <div className="p-8 text-center text-sm text-red-500">Backtest not found.</div>;

  return (
    <div className="space-y-4">
      <button onClick={() => navigate('/trading/quant/backtest')}
              className="text-xs uppercase tracking-wider text-gray-500 hover:text-brand-gold">
        ← Back
      </button>
      <h1 className="text-base font-serif">
        Backtest <span className="font-mono text-sm text-gray-500">{jobId.slice(0, 8)}…</span>
      </h1>

      <JobStatusBar job={job} type="backtest" />

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Tile label="Sharpe (net)" value={fmt(job.sharpe_net, 2)} colorize={numColor(job.sharpe_net)} />
        <Tile label="Annual return" value={pct(job.annual_return, 1)} colorize={numColor(job.annual_return)} />
        <Tile label="Max drawdown" value={pct(job.max_drawdown, 1)} colorize={numColor(job.max_drawdown)} />
        <Tile label="Annual turnover" value={pct(job.annual_turnover, 0)} />
        <Tile label="Alpha (annual)" value={pct(job.alpha_annual, 2)} colorize={numColor(job.alpha_annual)} />
        <Tile label="DSR" value={
          <span>
            {fmt(job.dsr, 2)}<br />
            {job.dsr_verdict && (
              <span className={`text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded ${VERDICT_COLORS[job.dsr_verdict] || ''}`}>
                {job.dsr_verdict}
              </span>
            )}
          </span>
        } />
      </div>

      {/* Cost breakdown */}
      {job.cost_spread_total != null && (
        <Section title="Costs Breakdown">
          <div className="grid grid-cols-3 gap-3 p-4">
            <CostTile label="Half-spread" value={`$${fmt(job.cost_spread_total, 0)}`} />
            <CostTile label="Market impact" value={`$${fmt(job.cost_impact_total, 0)}`} />
            <CostTile label="Borrow" value={`$${fmt(job.cost_borrow_total, 0)}`} />
          </div>
        </Section>
      )}

      {/* Factor attribution */}
      {job.factor_attribution && (
        <Section title="Factor Attribution">
          <table className="w-full text-sm">
            <tbody>
              {[
                ['Alpha (annualised)', job.factor_attribution.alpha_annual, true],
                ['Market β', job.factor_attribution.beta_market, false],
                ['Size β', job.factor_attribution.beta_size, false],
                ['Value β', job.factor_attribution.beta_value, false],
                ['Momentum β', job.factor_attribution.beta_momentum, false],
                ['Low-vol β', job.factor_attribution.beta_lowvol, false],
                ['R²', job.factor_attribution.r_squared, false],
              ].map(([label, value, alpha]) => (
                <tr key={label} className="border-t border-gray-100 dark:border-gray-800/50">
                  <td className="px-4 py-1.5 text-xs uppercase tracking-wider text-gray-500">{label}</td>
                  <td className={`px-4 py-1.5 text-right text-sm font-mono ${alpha ? numColor(value) : 'text-gray-700 dark:text-gray-300'}`}>
                    {alpha ? pct(value, 2) : fmt(value, 3)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Stress tests */}
      {job.stress_results && (
        <Section title="Stress Tests">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Period</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-right px-4 py-2">Cumulative</th>
                  <th className="text-right px-4 py-2">Sharpe</th>
                  <th className="text-right px-4 py-2">Max DD</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(job.stress_results).map(([name, r]) => (
                  <tr key={name} className="border-t border-gray-100 dark:border-gray-800/50">
                    <td className="px-4 py-1.5 font-mono text-xs">{name}</td>
                    <td className="px-4 py-1.5 text-xs">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                        r.type === 'historical_simulation'
                          ? 'bg-gray-100 text-gray-500 dark:bg-gray-800'
                          : 'bg-brand-gold/10 text-brand-gold'
                      }`}>
                        {r.type === 'historical_simulation' ? 'historical sim' : 'test period'}
                      </span>
                    </td>
                    <td className={`px-4 py-1.5 text-right text-xs font-mono ${numColor(r.cumulative_return)}`}>
                      {pct(r.cumulative_return, 1)}
                    </td>
                    <td className={`px-4 py-1.5 text-right text-xs font-mono ${numColor(r.sharpe)}`}>
                      {fmt(r.sharpe, 2)}
                    </td>
                    <td className={`px-4 py-1.5 text-right text-xs font-mono ${numColor(r.max_drawdown)}`}>
                      {pct(r.max_drawdown, 1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-[10px] text-gray-400 border-t border-gray-200 dark:border-gray-800">
            Historical simulations replay the frozen signal across crisis windows that fall outside the test period —
            informative but NOT a clean test result.
          </div>
        </Section>
      )}

      {/* DSR audit */}
      {job.n_trials_used != null && (
        <Section title="DSR Provenance">
          <div className="p-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Trials used (DSR n)</div>
              <div className="font-mono text-base">{job.n_trials_used.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Sharpe benchmark</div>
              <div className="font-mono text-base">{fmt(job.sharpe_benchmark, 3)}</div>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
};

const Tile = ({ label, value, colorize }) => (
  <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">{label}</div>
    <div className={`text-xl font-serif ${colorize || 'text-gray-800 dark:text-gray-200'}`}>{value}</div>
  </div>
);

const CostTile = ({ label, value }) => (
  <div className="text-center bg-gray-50 dark:bg-gray-800/50 rounded p-3">
    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{label}</div>
    <div className="text-sm font-mono">{value}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">{title}</h2>
    </div>
    {children}
  </div>
);

export default BacktestResultPage;
